import { type WritableDraft } from 'immer';

import { type PartialPropsOf } from '@blaze-runtime/engine-domain';
import { applyPropsPatch } from '@blaze-runtime/engine-domain';
import { type PropertiesConfigInputContainerItemActionWithIdModel } from '@blaze-shared/elements-domain';
import { type FormActionType, type Workflow } from '@blaze-shared/workflow-domain';

import { type EngineState } from '../../../+state/engine';
import { addEventWorkflowWithConnection } from '../../../ai';
import { getActionWorkflowContext } from '../../../ai/page/component-workflow-key';
import { patchTitle } from '../../../ai/page/prop-builders';

import { type FormDetailsResolvedContext, type ResolvedActions } from './form-details.definition';
import { FormElementHandler } from './form.element-handler';

export class FormDetailsElementHandler extends FormElementHandler<'formDetails'> {
  readonly type = 'formDetails';

  protected getFormProps(context: FormDetailsResolvedContext): PartialPropsOf<'formDetails'> {
    let props: PartialPropsOf<'formDetails'> = {
      content: {},
    };

    props = patchTitle<'formDetails'>(props, context.element.title, context.element.showTitle);

    if (context.element.iconName) {
      props = applyPropsPatch(props, {
        content: {
          title: {
            icon: context.element.iconName,
          },
        },
      });
    }

    if (context.workflow) {
      props = applyPropsPatch(props, {
        data: [
          {
            dataSource: {
              sourceType: 'context',
              id: context.workflow.id,
              columns: [],
            },
            key: 'value',
            bindings: {},
            enabled: true,
          },
        ],
      });
    }

    if (context.element.layout?.$type === 'custom') {
      props = applyPropsPatch(props, {
        content: { aiGeneratedFields: true },
      });
    }

    const columnCount = this.getColumnCount(context);
    if (columnCount) {
      props = applyPropsPatch(props, {
        content: {
          options: {
            columnsCount: columnCount,
          },
        },
      });
    }

    const { actions } = this.resolveActions(context);
    if (actions.length) {
      props = applyPropsPatch(props, {
        content: {
          headerActions: actions,
        },
      });
    }

    return props;
  }

  /**
   * A Details View's auto-layout `actions[]` become header-action buttons on the current row.
   * Action's workflows are in `componentWorkflows`, keyed by index (`<dvId>.Actions[<index>]`).
   */
  protected override registerActionWorkflows(
    context: FormDetailsResolvedContext,
    draft: WritableDraft<EngineState>,
  ): void {
    const { workflows } = this.resolveActions(context);

    if (!workflows.length) {
      return;
    }

    const entries = workflows.map((workflow) => {
      const { hasUpdates } = addEventWorkflowWithConnection(draft, context, workflow);
      return { workflow, forceUpdate: hasUpdates };
    });

    context.workflowsToFinalize = [...(context.workflowsToFinalize ?? []), ...entries];
  }

  protected getFormActionType(): FormActionType | undefined {
    return undefined;
  }

  protected getSubmitButtonTitleOverrides(): { label?: string; iconName?: string } | undefined {
    return undefined;
  }

  private resolveActions(context: FormDetailsResolvedContext): ResolvedActions {
    if (context.resolvedActions) {
      return context.resolvedActions;
    }

    const actions: PropertiesConfigInputContainerItemActionWithIdModel[] = [];
    const workflows: Workflow[] = [];

    (context.element.actions ?? []).forEach((action, index) => {
      const workflowContext = getActionWorkflowContext(
        context.element.id,
        context.componentWorkflows,
        'Actions',
        index,
      );

      actions.push({
        id: `${context.element.id}.Actions[${index}]`,
        label: action.name,
        icon: action.iconName,
        aiDescription: action.aiDescription,
        action: workflowContext ? { workflowId: workflowContext.workflow.id } : undefined,
      });

      if (workflowContext) {
        workflows.push(workflowContext.workflow);
      }
    });

    context.resolvedActions = { actions, workflows };
    return context.resolvedActions;
  }
}
